import { useState, useEffect, useCallback } from 'react';
import {
  handleThreadsLogin,
  handleThreadsLogout,
  isThreadsLoggedIn,
  getThreadsToken
} from '../utils/threadsOAuth';
import { useUser } from '../contexts/UserContext';

/**
 * Threads 인증 상태 관리 훅
 * 로그인 상태, 토큰 관리, 자동 갱신 등을 처리
 */
export const useThreadsAuth = () => {
  // UserContext에서 전역 로그인 상태 가져오기
  const { profile, isLogin: globalIsLogin, accessToken } = useUser();

  const [tokenData, setTokenData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 전역 상태를 우선 사용하되, OAuth 토큰 데이터는 별도 관리
  const isLoggedIn = globalIsLogin;
  const user = profile ? { id: profile.id, ...profile } : null;

  // OAuth 토큰 데이터 초기화 (전역 상태가 있을 때만)
  useEffect(() => {
    if (globalIsLogin && accessToken) {
      try {
        const token = getThreadsToken();
        setTokenData(token || { access_token: accessToken, user_id: profile?.id });
        console.log('🔍 토큰 데이터 동기화:', { hasToken: !!token, globalLogin: globalIsLogin });
      } catch (error) {
        console.error('❌ 토큰 데이터 동기화 실패:', error);
        // 전역 상태가 있으면 기본 토큰 데이터 생성
        if (accessToken && profile?.id) {
          setTokenData({ access_token: accessToken, user_id: profile.id });
        }
      }
    } else if (!globalIsLogin) {
      setTokenData(null);
    }
  }, [globalIsLogin, accessToken, profile]);

  // 로그아웃 함수 (OAuth 토큰만 정리, 전역 로그아웃은 별도 처리)
  const logout = useCallback(() => {
    try {
      console.log('🔒 OAuth 토큰 정리');
      handleThreadsLogout();

      setTokenData(null);
      setError(null);

      console.log('✅ OAuth 로그아웃 완료');
    } catch (error) {
      console.error('❌ OAuth 로그아웃 중 오류:', error);
      setError('로그아웃 중 오류가 발생했습니다.');
    }
  }, []);

  // 토큰 만료 확인 및 자동 갱신
  useEffect(() => {
    if (!tokenData || !tokenData.expires_in) return;

    const checkTokenExpiry = () => {
      const loginTime = localStorage.getItem('threads_login_time');
      if (!loginTime) return;

      const expiryTime = parseInt(loginTime) + (tokenData.expires_in * 1000);
      const currentTime = Date.now();
      const timeUntilExpiry = expiryTime - currentTime;

      console.log('⏰ 토큰 만료까지:', Math.floor(timeUntilExpiry / (1000 * 60 * 60)), '시간');

      // 토큰이 1시간 남았을 때 갱신 알림
      if (timeUntilExpiry > 0 && timeUntilExpiry < 60 * 60 * 1000) {
        console.warn('⚠️ 토큰이 곧 만료됩니다. 갱신을 고려하세요.');
      }

      // 토큰이 만료된 경우
      if (timeUntilExpiry <= 0) {
        console.warn('🔒 토큰이 만료되었습니다. 재로그인이 필요합니다.');
        logout();
      }
    };

    // 초기 확인
    checkTokenExpiry();

    // 1시간마다 토큰 상태 확인
    const interval = setInterval(checkTokenExpiry, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [tokenData, logout]);

  // 로그인 함수 (전역 상태가 없을 때만 OAuth 로그인 수행)
  // redirectPath가 주어지면 세션에 저장하여 콜백 후 해당 경로로 이동하도록 함
  const login = useCallback(async (redirectPath) => {
    // 이미 전역적으로 로그인되어 있으면 OAuth 로그인 불필요
    if (globalIsLogin && accessToken) {
      console.log('✅ 이미 전역 로그인 상태입니다');
      return { access_token: accessToken, user_id: profile?.id };
    }

    try {
      setIsLoading(true);
      setError(null);

      // 선택적 리다이렉트 경로 저장 (콜백에서 사용)
      if (redirectPath) {
        try {
          sessionStorage.setItem('login_redirect_to', redirectPath);
        } catch (_) {}
      }

      console.log('🚀 OAuth 로그인 시작');
      const token = await handleThreadsLogin();

      setTokenData(token);

      console.log('✅ OAuth 로그인 성공');
      return token;
    } catch (error) {
      console.error('❌ OAuth 로그인 실패:', error);
      setError(error.message || '로그인에 실패했습니다.');
      setTokenData(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [globalIsLogin, accessToken, profile]);

  

  // 토큰 유효성 검증 (전역 상태 우선)
  const validateToken = useCallback(async () => {
    // 전역 로그인 상태가 있으면 유효한 것으로 간주
    if (globalIsLogin && accessToken) {
      return true;
    }

    if (!tokenData || !tokenData.access_token) {
      return false;
    }

    try {
      // Threads API로 토큰 유효성 확인 (실제 구현 시)
      // const response = await fetch(`https://graph.threads.net/v1.0/me?access_token=${tokenData.access_token}`);
      // return response.ok;

      // 개발 환경에서는 localStorage 기반으로 간단 확인
      return isThreadsLoggedIn();
    } catch (error) {
      console.error('❌ 토큰 검증 실패:', error);
      return false;
    }
  }, [globalIsLogin, accessToken, tokenData]);

  // 에러 초기화
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // API 호출을 위한 헤더 생성 (전역 토큰 우선)
  const getAuthHeaders = useCallback(() => {
    const token = accessToken || tokenData?.access_token;

    if (!token) {
      throw new Error('인증 토큰이 없습니다. 로그인이 필요합니다.');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, [accessToken, tokenData]);

  // Threads API 호출 wrapper
  const threadsApiCall = useCallback(async (endpoint, options = {}) => {
    try {
      const headers = getAuthHeaders();
      
      const response = await fetch(`https://graph.threads.net/v1.0/${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // 인증 만료
          logout();
          throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.');
        }
        throw new Error(`API 호출 실패: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Threads API 호출 실패:', error);
      throw error;
    }
  }, [getAuthHeaders, logout]);

  return {
    // 상태
    isLoggedIn,
    isLoading,
    error,
    tokenData,
    user,
    
    // 액션
    login,
    logout,
    clearError,
    validateToken,
    
    // 유틸리티
    getAuthHeaders,
    threadsApiCall,
    
    // 상태 체크 (전역 상태 우선)
    hasValidToken: !!(accessToken || (tokenData && tokenData.access_token)),
    isTokenExpired: globalIsLogin ? false : (tokenData ? (Date.now() - parseInt(localStorage.getItem('threads_login_time') || '0')) > (tokenData.expires_in * 1000) : false),
  };
};